import React, { useEffect, useState } from 'react';
import VBuck from './VBuck';
import { Ticket } from 'lucide-react';
import { nextDrawDate, timeLeft, WEEKLY_PRIZE } from '../lib/lottery';

export default function LotteryBadge({ entries = 0, weeklyPrize = WEEKLY_PRIZE, targetDate, onScrollTo, size = 132 }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = targetDate instanceof Date ? targetDate : nextDrawDate(now);
  const { days, hours, minutes } = timeLeft(target, now);

  const formatTime = () =>
    days > 0
      ? `${days}d ${hours}h`
      : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  const shortPrize = (() => {
    if (weeklyPrize >= 1000) {
      const k = weeklyPrize / 1000;
      return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
    }
    return weeklyPrize.toString();
  })();

  // Proportional sizing from base 132 → size
  const scale = size / 132;
  const coinSize = Math.round(62 * scale);
  const winFont = Math.max(10, Math.round(11 * scale));
  const priceFont = Math.max(11, Math.round(12 * scale));
  const bubbleFont = Math.max(10, Math.round(11 * scale));
  const bubbleH = Math.max(20, Math.round(24 * scale));

  return (
    <button
      type="button"
      onClick={onScrollTo}
      className="group relative shrink-0"
      style={{ width: size, height: size }}
      aria-label="View V-Bucks weekly drawing"
      title={`Weekly drawing · ${weeklyPrize.toLocaleString()} V-Bucks · ${entries} entries`}
    >
      {/* "Peel" shadow behind the sticker */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full"
        style={{
          background: '#3a1064',
          transform: 'translate(4px, 7px) rotate(-8deg)',
          opacity: 0.35,
          filter: 'blur(1px)',
        }}
      />

      {/* Sticker body — tilted, scalloped border via outer ring */}
      <span
        className="absolute inset-0 rounded-full transition-transform duration-300 group-hover:rotate-0 group-hover:scale-105 group-active:scale-95"
        style={{
          transform: 'rotate(-8deg)',
        }}
      >
        {/* Outer star/burst ring */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full animate-pulse-slow"
          style={{
            background:
              'conic-gradient(from 0deg, #ffd900, #ff2bd6, #00b8ff, #2ee6c1, #ffd900)',
            padding: Math.max(4, Math.round(6 * scale)),
            WebkitMask:
              'radial-gradient(circle, transparent 58%, #000 60%, #000 100%)',
            mask: 'radial-gradient(circle, transparent 58%, #000 60%, #000 100%)',
          }}
        />

        {/* Main sticker circle */}
        <span
          className="absolute rounded-full border-[3px] flex items-center justify-center"
          style={{
            inset: Math.max(4, Math.round(6 * scale)),
            background:
              'radial-gradient(circle at 30% 25%, #ffffff 0%, #ffd900 45%, #ff9a1c 100%)',
            borderColor: '#3a1064',
            boxShadow:
              'inset 0 -3px 0 rgba(0,0,0,0.18), 0 5px 0 #3a1064, 0 0 30px rgba(255,214,0,0.6)',
          }}
        >
          {/* "WIN" tag at top — pushed higher so the countdown/entry bubbles below don't touch it */}
          <span
            className="absolute left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md font-black tracking-[2px] uppercase"
            style={{
              top: -Math.round(22 * scale),
              fontSize: winFont,
              background: '#ff2bd6',
              color: '#ffffff',
              border: '2px solid #3a1064',
              boxShadow: '0 2px 0 #3a1064',
              zIndex: 3,
            }}
          >
            Win
          </span>

          {/* Spinning V-Bucks coin in center */}
          <span className="inline-flex items-center justify-center animate-spin-slow">
            <VBuck size={coinSize} />
          </span>

          {/* Bottom price tag */}
          <span
            className="absolute left-1/2 -translate-x-1/2 px-3 py-1 rounded-md font-black inline-flex items-center gap-1"
            style={{
              bottom: -Math.round(12 * scale),
              fontSize: priceFont,
              background: '#3a1064',
              color: '#ffd900',
              border: '2px solid #ffd900',
              boxShadow: '0 2px 0 #1a0440',
              whiteSpace: 'nowrap',
            }}
          >
            <VBuck size={Math.round(priceFont * 1.05)} />
            {shortPrize}
          </span>
        </span>
      </span>

      {/* Entry count bubble (top-right) */}
      <span
        className="absolute inline-flex items-center gap-0.5 px-2 rounded-full font-black"
        style={{
          right: -2,
          top: Math.round(6 * scale),
          height: bubbleH,
          fontSize: bubbleFont,
          background: '#00b8ff',
          color: '#ffffff',
          border: '2px solid #3a1064',
          boxShadow: '0 2px 0 #3a1064',
          zIndex: 2,
        }}
      >
        <Ticket style={{ width: bubbleFont, height: bubbleFont }} />
        {entries}
      </span>

      {/* Countdown bubble (top-left, opposite of entries) */}
      <span
        className="absolute inline-flex items-center px-2 rounded-full font-black tracking-wider"
        style={{
          left: -2,
          top: Math.round(6 * scale),
          height: bubbleH,
          fontSize: bubbleFont,
          background: '#ffffff',
          color: '#3a1064',
          border: '2px solid #3a1064',
          boxShadow: '0 2px 0 #3a1064',
          zIndex: 2,
        }}
      >
        {formatTime()}
      </span>
    </button>
  );
}
